import dayjs from "dayjs";
import { EMPTY_PROVIDER_DIRECTORY, providerFilterModel } from "../utils/providerDirectory";

/* Statuses that mean the patient is in the building. The sheet's "Signed in"
   view and its counts are built from this list. */
export const SIGNED_IN_STATUSES = [
  "Ready for Triage",
  "Processing",
  "Ready for Checkout",
  "Ready for Provider",
  "Walk-in",
  "Speak with Nurse",
  "Labs/Injections",
  "Speak with Representative",
  "Tele - Confirmed",
];

/* Desk requests that used to tint the row orange; they still do. */
export const FLAGGED_STATUSES = [
  "Walk-in",
  "Speak with Nurse",
  "Labs/Injections",
  "Speak with Representative",
];

// Visits that are over: neither signed in nor still expected.
const CLOSED_STATUSES = ["Checked Out", "Cancelled", "No Show", "Rescheduled"];

/* The status chips above the table, in order. */
export const STATUS_CHIPS = [
  "Processing",
  "Ready for Triage",
  "Ready for Provider",
  "Ready for Checkout",
  "Checked Out",
];

const STATUS_META = {
  Processing: { label: "Processing", tone: "blue" },
  "Ready for Triage": { label: "Ready for triage", tone: "green" },
  "Ready for Provider": { label: "Ready for provider", tone: "purple" },
  "Ready for Checkout": { label: "Ready for checkout", tone: "indigo" },
  "Checked Out": { label: "Checked out", tone: "done" },
  Cancelled: { label: "Cancelled", tone: "red" },
  "No Show": { label: "No show", tone: "red" },
  Rescheduled: { label: "Rescheduled", tone: "red" },
};

/* Label and colour tone for a visit_confirmation value. */
export const statusMeta = (status) => {
  if (STATUS_META[status]) return STATUS_META[status];
  if (SIGNED_IN_STATUSES.includes(status)) return { label: status, tone: "orange" };
  return { label: status || "Not arrived", tone: "grey" };
};

export const isSignedIn = (row) => SIGNED_IN_STATUSES.includes(row.visit_confirmation);
export const isExpected = (row) =>
  !isSignedIn(row) && !CLOSED_STATUSES.includes(row.visit_confirmation);

export const checkinAt = (row) => {
  const t = row?.signintime?.ReadyForTriageTime;
  return t ? dayjs(t) : null;
};

export const providersOf = (row) =>
  row.linked_provider
    ? row.linked_provider.split("/").map((p) => p.trim()).filter(Boolean)
    : [];

/* Provider filter keyed by provider ID (owner 2026-10-08, item 8: "Yes pls
   switch those as well"), the same model as the dashboard
   (utils/providerDirectory.providerFilterModel): each row's key is its
   EFFECTIVE provider id ("id:19") - a linked_provider that names a
   provider_master provider (a Select Provider reassignment) wins, else the
   booked providerid, with the practice's id aliases applied (9928: 16 -> 15,
   Leanora) - and only a row nothing resolves keeps a "name:" bucket. So one
   provider stored under several names ("Bobby A Malik, MD" / "Bobby Malik")
   is one entry and selecting it shows every one of their rows.
   A combined "A / B" row (the sheet's Select Provider can save two names)
   keys each name on its own - as a reassignment name, never with the booked
   id, which belongs to one of them - so it shows under both providers.
   Display only: nothing is written back. */
const expandSigninRow = (row) => {
  const parts = providersOf(row || {});
  if (parts.length <= 1) return [row];
  return parts.map((p) => ({ linked_provider: p }));
};

/* { keysOf(row) -> [key], options: [{ value, label, name, names }] } for the
   loaded rows. `doctorLabels` false (OSA): the label is the provider's name
   (provider_master, else the rows' most used spelling), never "Dr. <Last>". */
export const signinProviderModel = (
  rows,
  directory = EMPTY_PROVIDER_DIRECTORY,
  { doctorLabels = true, aliases = null } = {},
) => {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  const model = providerFilterModel(list.flatMap(expandSigninRow), directory, { doctorLabels, aliases });
  const keysOf = (row) =>
    Array.from(new Set(expandSigninRow(row).map((r) => model.keyOf(r)).filter(Boolean)));
  return { keysOf, options: model.options };
};

export const roomOf = (row) => {
  const room = String(row.room_no || "").trim();
  return room && room !== "0" ? room : "";
};
export const inRoom = (row) => {
  const room = roomOf(row);
  return Boolean(room) && room !== "Waiting Room";
};

/* Minutes since check-in, or null before check-in. */
export const waitMinutes = (row, now) => {
  const at = checkinAt(row);
  if (!at) return null;
  return Math.max(0, Math.floor((now.getTime() - at.valueOf()) / 60000));
};

/* "50 min", "1 h 06 min". */
export const formatWait = (minutes) => {
  if (minutes == null) return "";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min`;
};

/* "16:15:00" -> "4:15 PM". */
export const formatApptTime = (starttime) => {
  if (!starttime) return "—";
  const [h, m] = starttime.split(":");
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  if (hour > 12) hour -= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${m} ${ampm}`;
};
